/**
 * useUserPuck — single source of truth for the user's smoothed position +
 * heading on a Google Map. Wraps the swappable Web providers; native
 * providers can plug in at construction time later without UI churn.
 *
 *   const puck = useUserPuck({ active });
 *   puck.lat, puck.lng     — Kalman-smoothed coords
 *   puck.heading           — circular-smoothed heading (deg, north=0)
 *   puck.headingConfident  — true when accuracyDeg < 25
 *   puck.permission        — "granted" | "prompt" | "denied" | "unavailable"
 *   puck.request()         — kicks off the permission flow (inside gesture)
 *   puck.hasFix            — at least one GPS fix received
 *
 * `active=false` tears down the sensors to save battery while the map is
 * out of view.
 */
import { useCallback, useEffect, useRef, useState } from "react";

import { WebLocationProvider } from "./location/LocationProvider";
import { GPSKalmanFilter } from "./location/KalmanFilter";
import { WebHeadingProvider, HeadingSmoother } from "./heading/HeadingProvider";

export default function useUserPuck({ active = true, providers } = {}) {
  // Default providers are the Web ones; the call site may inject native
  // providers later via the `providers` prop.
  const locProviderRef = useRef(null);
  const hdgProviderRef = useRef(null);
  const kalmanRef = useRef(new GPSKalmanFilter({ processNoise: 0.6 }));
  const hdgSmootherRef = useRef(new HeadingSmoother({ alpha: 0.28 }));

  if (!locProviderRef.current) {
    locProviderRef.current = providers?.location || new WebLocationProvider({ minIntervalMs: 1000 });
  }
  if (!hdgProviderRef.current) {
    hdgProviderRef.current = providers?.heading || new WebHeadingProvider();
  }

  const [state, setState] = useState({
    lat: null,
    lng: null,
    accuracy: null,
    speedMps: null,
    heading: 0,
    headingAccuracyDeg: 90,
    headingConfident: false,
    hasFix: false,
    permission: "prompt",
  });

  // Mount/unmount sensor subscriptions when `active` toggles.
  useEffect(() => {
    if (!active) return undefined;
    const loc = locProviderRef.current;
    const hdg = hdgProviderRef.current;
    const unsubLoc = loc.subscribe((sample) => {
      const { lat, lng } = kalmanRef.current.update(sample);
      setState((s) => {
        // Skip re-render when the Kalman output is identical (or sub-cm)
        // — avoids the cascading panTo / lerp restart in LocationPuck
        // while the user is stationary.
        if (s.lat !== null &&
            Math.abs(lat - s.lat) < 1e-7 &&
            Math.abs(lng - s.lng) < 1e-7) {
          return s.hasFix ? s : { ...s, hasFix: true };
        }
        return {
          ...s,
          lat,
          lng,
          accuracy: sample.accuracy,
          speedMps: sample.speedMps,
          heading: s.headingAccuracyDeg >= 60 && typeof sample.headingDeg === "number"
            ? sample.headingDeg
            : s.heading,
          hasFix: true,
        };
      });
    });
    const unsubHdg = hdg.subscribe(({ headingDeg, accuracyDeg }) => {
      const smoothed = hdgSmootherRef.current.push(headingDeg);
      setState((s) => {
        // Don't trigger a React re-render for sub-perceptual heading
        // changes (< 2°) or accuracy band micro-fluctuations. The whole
        // pipeline (LocationPuck effect, marker rotation, optional map
        // heading) reads these — gating here saves dozens of renders/sec.
        const dh = Math.abs(((smoothed - s.heading + 540) % 360) - 180);
        const confidentNow = accuracyDeg < 25;
        if (dh < 2 && confidentNow === s.headingConfident) return s;
        return {
          ...s,
          heading: smoothed,
          headingAccuracyDeg: accuracyDeg,
          headingConfident: confidentNow,
        };
      });
    });
    return () => {
      unsubLoc?.();
      unsubHdg?.();
      kalmanRef.current.reset();
      hdgSmootherRef.current.reset();
    };
  }, [active]);

  // Initial permission probe (location only — heading needs a user gesture).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const p = await locProviderRef.current.requestPermission();
      if (!cancelled) setState((s) => ({ ...s, permission: p }));
    })();
    return () => { cancelled = true; };
  }, []);

  // Called from a user-gesture handler (locate-me button). Requests
  // both location and compass permission. Returns the resolved location
  // permission state so the caller can decide whether to recenter.
  const request = useCallback(async () => {
    // Heading first — must be inside the gesture stack on iOS Safari.
    await hdgProviderRef.current.requestPermission();
    const p = await locProviderRef.current.requestPermission();
    setState((s) => ({ ...s, permission: p }));
    return p;
  }, []);

  return { ...state, request };
}
